import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Mail, Lock, User, Crown, CheckCircle, ArrowLeft } from "lucide-react";

const AuthModal = ({ isOpen, onClose, mode, setMode }) => {
  const { login, register, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
  });

  // Check user role and redirect accordingly
  const checkRoleAndRedirect = async (authToken) => {
    try {
      const response = await axios.get(`${API}/founders/check`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const role = response.data.role;
      if (role === "driver") {
        navigate("/driver");
        return true;
      } else if (role === "ordering") {
        navigate("/ordering-portal");
        return true;
      } else if (role === "founder") {
        navigate("/founders");
        return true;
      }
    } catch (error) {
      console.log("Role check failed:", error);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === "login") {
        const response = await login(formData.email, formData.password);
        toast.success("Welcome back!");
        onClose();
        setFormData({ email: "", password: "", fullName: "" });
        
        // Check role and redirect
        if (response.token) {
          await checkRoleAndRedirect(response.token);
        }
      } else {
        await register(formData.email, formData.password, formData.fullName);
        toast.success("Account created successfully!");
        onClose();
        setFormData({ email: "", password: "", fullName: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
      setResetSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch (error) {
      toast.error("Failed to send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const memberBenefits = [
    "4% convenience fee (vs 7% for guests)",
    "$15 fee cap on all orders",
    "Order history and tracking",
    "Faster checkout experience",
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { setForgotMode(false); setResetSent(false); } onClose(); }}>
      <DialogContent className="sm:max-w-[425px]" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold font-['Outfit']">
            {forgotMode ? "Reset Password" : mode === "login" ? "Welcome Back" : "Create Account"}
          </DialogTitle>
        </DialogHeader>

        {forgotMode ? (
          resetSent ? (
            <div className="text-center py-6" data-testid="reset-sent-message">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Check your email</h3>
              <p className="text-slate-600 text-sm mb-6">
                If an account exists for <strong>{formData.email}</strong>, we've sent a password reset link.
              </p>
              <Button variant="outline" onClick={() => { setForgotMode(false); setResetSent(false); }} data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">Enter your email and we'll send you a link to reset your password.</p>
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="reset-email" type="email" placeholder="you@example.com" className="pl-10" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="input-reset-email" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary h-11" disabled={loading} data-testid="send-reset-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Reset Link
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setForgotMode(false)} className="text-sky-600 text-sm font-medium hover:underline" data-testid="back-to-login-link">
                  <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Sign In
                </button>
              </div>
            </form>
          )
        ) : (
        <>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {mode === "register" && (
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  className="pl-10"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  data-testid="input-fullname"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                className="pl-10"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="input-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                className="pl-10"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                data-testid="input-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full btn-primary h-11"
            disabled={loading}
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : null}
            {mode === "login" ? "Sign In" : "Create Account"}
          </Button>
        </form>

        {mode === "login" && (
          <div className="text-center -mt-2">
            <button type="button" onClick={() => setForgotMode(true)} className="text-sky-600 text-sm font-medium hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </button>
          </div>
        )}

        {/* Member Benefits */}
        {mode === "register" && (
          <div className="mt-6 p-4 bg-sky-50 rounded-xl border border-sky-100">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-5 h-5 text-sky-600" />
              <span className="font-semibold text-slate-900">Member Benefits - $5/month</span>
            </div>
            <ul className="space-y-2">
              {memberBenefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Fee Information */}
        <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
          <p className="font-medium text-slate-700 mb-1">Fee Structure:</p>
          <p>• Guest: 7% convenience fee, capped at $15 max</p>
          <p>• Member: 4% convenience fee, capped at $15 max</p>
        </div>

        {/* Switch Mode */}
        <div className="mt-4 text-center text-sm">
          <span className="text-slate-500">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}
          </span>{" "}
          <button
            type="button"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-sky-600 font-medium hover:underline"
            data-testid="auth-switch-mode"
          >
            {mode === "login" ? "Sign Up" : "Sign In"}
          </button>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
